import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, LogOut, RefreshCw, AlertTriangle, TrendingDown } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { API } from "@/lib/okkio";
import NoIndex from "@/components/NoIndex";

const TOKEN_KEY = "okkio_admin_token";

const EVENT_LABELS = {
  landing_view: "Landing view",
  upload_start: "Upload avviati",
  upload_complete: "Upload completati",
  analyze_start: "Analisi avviate",
  not_eligible: "Non idonei",
  teaser_view: "Teaser visti",
  checkout_start: "Checkout avviati",
  unlock_click: "Click sblocca",
  payment_success: "Pagamenti OK",
  payment_failed: "Pagamenti falliti",
  payment_expired: "Pagamenti scaduti",
  full_report_ready: "Report pronti",
  pdf_download: "PDF scaricati",
};

const RANGES = [7, 14, 30, 90];

function Login({ onSubmit, error }) {
  const [val, setVal] = useState("");
  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-5">
      <NoIndex />
      <form
        onSubmit={(e) => { e.preventDefault(); onSubmit(val.trim()); }}
        className="w-full max-w-sm bg-white rounded-2xl p-7 shadow-2xl"
        data-testid="admin-login-form"
      >
        <h1 className="font-heading text-xl font-semibold text-[#0F172A]">OKKIO · Metriche</h1>
        <p className="mt-1 text-sm text-slate-500">Area riservata. Inserisci il token admin.</p>
        <input
          type="password"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          placeholder="Token admin"
          autoFocus
          data-testid="admin-token-input"
          className="mt-5 w-full h-12 rounded-xl border border-[#E2E8F0] px-4 text-sm focus:border-[#3157F6] focus:outline-none"
        />
        {error && (
          <p className="mt-2 text-sm text-[#DC3F4C]" data-testid="admin-login-error">
            Token non valido o accesso negato.
          </p>
        )}
        <button
          type="submit"
          data-testid="admin-login-submit"
          className="mt-5 w-full h-12 rounded-xl bg-[#3157F6] hover:bg-[#2444D8] text-white font-semibold transition-colors"
        >
          Accedi
        </button>
      </form>
    </div>
  );
}

function KpiCard({ k, value, highlight }) {
  return (
    <div
      data-testid={`kpi-card-${k}`}
      className={`rounded-xl border p-4 ${
        highlight ? "border-[#DC3F4C]/40 bg-[#DC3F4C]/5" : "border-[#E2E8F0] bg-white"
      }`}
    >
      <div className="text-xs font-medium text-slate-500">{EVENT_LABELS[k] || k}</div>
      <div className={`mt-1 text-2xl font-bold ${highlight ? "text-[#DC3F4C]" : "text-[#0F172A]"}`}>
        {value}
      </div>
    </div>
  );
}

export default function AdminMetrics() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (tok, d) => {
    setLoading(true);
    try {
      const [res, det] = await Promise.all([
        axios.get(`${API}/admin/funnel?days=${d}`, { headers: { "x-admin-token": tok } }),
        axios.get(`${API}/admin/reports?days=${d}`, { headers: { "x-admin-token": tok } }),
      ]);
      setData(res.data);
      setDetail(det.data);
      setAuthed(true);
      setLoginError(false);
      localStorage.setItem(TOKEN_KEY, tok);
    } catch {
      setLoginError(true);
      setAuthed(false);
      setData(null);
      setDetail(null);
      localStorage.removeItem(TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchData(token, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authed) fetchData(token, days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setData(null);
  };

  if (!authed) {
    return (
      <Login
        error={loginError}
        onSubmit={(t) => { setToken(t); fetchData(t, days); }}
      />
    );
  }

  const kpi = data?.kpi || {};
  const funnel = data?.funnel || [];
  const daily = data?.daily || [];
  const alerts = data?.alerts || {};

  return (
    <div className="min-h-screen bg-[#F7F9FC]">
      <NoIndex />
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-6xl mx-auto px-5 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-lg font-semibold">OKKIO · Metriche</h1>
            {data?.range && (
              <p className="text-xs text-slate-300">
                {data.range.from} → {data.range.to} · {data.range.timezone}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchData(token, days)}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 h-9 text-sm transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 px-3 h-9 text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Esci
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6">
        {/* Range selector */}
        <div className="flex items-center gap-2" data-testid="admin-range-selector">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              data-testid={`range-${r}`}
              className={`rounded-full px-4 h-9 text-sm font-semibold transition-colors ${
                days === r
                  ? "bg-[#3157F6] text-white"
                  : "bg-white border border-[#E2E8F0] text-[#334155] hover:border-[#3157F6]"
              }`}
            >
              {r} giorni
            </button>
          ))}
        </div>

        {/* Alerts */}
        {alerts.zero_pagamenti && (
          <div
            data-testid="alert-zero-payments"
            className="mt-5 flex items-center gap-2 rounded-xl border border-[#DC3F4C]/40 bg-[#DC3F4C]/5 px-4 py-3 text-sm font-medium text-[#DC3F4C]"
          >
            <AlertTriangle className="w-4 h-4" /> Nessun pagamento nell'intervallo selezionato.
          </div>
        )}
        {alerts.drop_maggiore && (
          <div
            data-testid="alert-drop"
            className="mt-3 flex items-center gap-2 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/5 px-4 py-3 text-sm font-medium text-[#B45309]"
          >
            <TrendingDown className="w-4 h-4" /> Maggior drop-off: {EVENT_LABELS[alerts.drop_maggiore.da] || alerts.drop_maggiore.da} → {EVENT_LABELS[alerts.drop_maggiore.a] || alerts.drop_maggiore.a} ({alerts.drop_maggiore.drop_pct}%)
          </div>
        )}

        {/* KPI cards */}
        <h2 className="mt-6 text-base font-semibold text-[#0F172A]">KPI assoluti</h2>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="kpi-grid">
          {Object.keys(EVENT_LABELS).map((k) => (
            <KpiCard key={k} k={k} value={kpi[k] ?? 0} highlight={k === "payment_success" && (kpi[k] ?? 0) === 0} />
          ))}
        </div>

        {/* Funnel */}
        <h2 className="mt-8 text-base font-semibold text-[#0F172A]">Funnel di conversione</h2>
        <div className="mt-3 space-y-2" data-testid="funnel-list">
          {funnel.map((f) => (
            <div key={f.step} className="rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid={`funnel-${f.step}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[#0F172A]">{EVENT_LABELS[f.step] || f.step}</span>
                <span className="text-sm font-bold text-[#0F172A]">{f.count}</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-[#3157F6] rounded-full"
                  style={{ width: `${Math.min(f.pct_da_inizio, 100)}%` }}
                />
              </div>
              <div className="mt-1.5 flex items-center justify-between text-xs text-slate-500">
                <span>da inizio: {f.pct_da_inizio}%</span>
                <span>da step precedente: {f.pct_da_precedente}%</span>
              </div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <h2 className="mt-8 text-base font-semibold text-[#0F172A]">Andamento giornaliero</h2>
        <div className="mt-3 rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="daily-chart">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={daily} margin={{ top: 8, right: 16, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="landing_view" name="Landing" stroke="#3157F6" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="analyze_start" name="Analisi" stroke="#16A36A" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="teaser_view" name="Teaser" stroke="#F59E0B" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="payment_success" name="Pagamenti" stroke="#DC3F4C" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Daily table */}
        <h2 className="mt-8 text-base font-semibold text-[#0F172A]">Tabella giornaliera</h2>
        <div className="mt-3 overflow-x-auto rounded-xl border border-[#E2E8F0] bg-white" data-testid="daily-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Data</th>
                {Object.keys(EVENT_LABELS).map((k) => (
                  <th key={k} className="px-3 py-2 text-right font-medium whitespace-nowrap">{EVENT_LABELS[k]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {daily.map((row) => (
                <tr key={row.date} className="border-t border-[#F1F5F9]">
                  <td className="px-3 py-2 font-medium text-[#0F172A] whitespace-nowrap">{row.date}</td>
                  {Object.keys(EVENT_LABELS).map((k) => (
                    <td key={k} className="px-3 py-2 text-right text-slate-600">{row[k] ?? 0}</td>
                  ))}
                </tr>
              ))}
              {daily.length === 0 && (
                <tr><td colSpan={Object.keys(EVENT_LABELS).length + 1} className="px-3 py-6 text-center text-slate-400">Nessun dato nell'intervallo.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="mt-6 mb-6 text-xs text-slate-400">{data?.dedup_criterio}</p>

        {/* Detail: classificazione anonimizzata upload */}
        <h2 className="mt-4 text-base font-semibold text-[#0F172A]">Dettaglio upload (anonimizzato)</h2>
        {detail && (
          <p className="mt-1 text-xs text-slate-500" data-testid="detail-summary">
            {detail.totale} report · badge: <span className="font-semibold text-[#B45309]">{detail.badge_summary?.test || 0} test</span> · <span className="font-semibold text-[#16A36A]">{detail.badge_summary?.utente || 0} utente</span>
          </p>
        )}
        <div className="mt-3 overflow-x-auto rounded-xl border border-[#E2E8F0] bg-white" data-testid="detail-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                {["Badge", "Data", "Hash", "File", "Tipologia", "Classificazione", "Voci", "Incongr.", "Mancanti", "Importo €", "Comune", "Idoneità", "Rischio", "Teaser", "Checkout", "Sess.", "Pagato", "Scaduto", "PDF"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(detail?.items || []).map((it) => (
                <tr key={it.hash + it.created_at} className="border-t border-[#F1F5F9]" data-testid={`detail-row-${it.hash}`}>
                  <td className="px-3 py-2">
                    <span
                      title={it.test_evidence?.join(", ") || "nessuna evidenza test"}
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                        it.badge === "test"
                          ? "bg-[#F59E0B]/15 text-[#B45309]"
                          : "bg-[#16A36A]/10 text-[#16A36A]"
                      }`}
                    >
                      {it.badge === "test" ? "TEST" : "UTENTE"}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{(it.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-400">{it.hash}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{it.file_ext || "—"}{it.file_size_bytes ? ` · ${Math.round(it.file_size_bytes / 1024)}KB` : ""}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{it.tipologia_lavori || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap font-medium text-[#0F172A]">{it.classificazione_documento || "—"}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{it.num_voci ?? "—"}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{it.num_incongruenze_prezzo ?? "—"}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{it.num_lavorazioni_mancanti ?? "—"}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{it.importo_totale != null ? Number(it.importo_totale).toLocaleString("it-IT") : "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{it.comune || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{it.eligibility_stato || (it.status === "not_eligible" ? it.eligibility_motivo : "idoneo")}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{it.livello_rischio || "—"}{it.punteggio != null ? ` (${it.punteggio})` : ""}</td>
                  <td className="px-3 py-2 text-center">{it.teaser_view ? "✓" : "—"}</td>
                  <td className="px-3 py-2 text-center">{it.checkout_start ? "✓" : "—"}</td>
                  <td className="px-3 py-2 text-center text-slate-600">{it.n_sessioni_stripe || 0}</td>
                  <td className="px-3 py-2 text-center">{it.payment_success ? <span className="text-[#16A36A] font-semibold">✓{it.importo_pagamento ? ` €${it.importo_pagamento}` : ""}</span> : "—"}</td>
                  <td className="px-3 py-2 text-center">{it.payment_expired ? <span className="text-[#DC3F4C]">✓</span> : "—"}</td>
                  <td className="px-3 py-2 text-center">{it.pdf_download ? "✓" : "—"}</td>
                </tr>
              ))}
              {(detail?.items || []).length === 0 && (
                <tr><td colSpan={19} className="px-3 py-6 text-center text-slate-400">Nessun report nell'intervallo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {detail?._limiti && (
          <details className="mt-3 mb-10 text-xs text-slate-500" data-testid="detail-limits">
            <summary className="cursor-pointer font-medium text-slate-600">Privacy e limiti di correlazione</summary>
            <div className="mt-2">
              <p className="font-medium">Campi esclusi per privacy:</p>
              <p className="text-slate-400">{(detail._privacy_escluso || []).join(", ")}</p>
              <p className="mt-2 font-medium">Limiti:</p>
              <ul className="list-disc pl-5 text-slate-400 space-y-0.5">
                {detail._limiti.map((l, i) => <li key={i}>{l}</li>)}
              </ul>
            </div>
          </details>
        )}
      </main>
    </div>
  );
}
